
/**
 * API: Bambuser
 * -----------------------------------------------------------------------------
 * - API to interact with Bambuser / Live Shopping SDK.
 * - https://bambuser.com/docs/one-to-many/player-api-reference/
 * @namespace Bambuser
 */
import { getItemProperties } from '~/helpers/product'
import { stringToHandle } from '~/helpers/strings'

import BambuserButton from '~components/bambuser-button/bambuser-button'

export default {

  /**
   * Initializes Bambuser Live Shopping script.
   */
  initializeBambuserScript() {
    window.initBambuserLiveShopping = (item) => {
      window.initBambuserLiveShopping.queue.push(item)
    }

    window.initBambuserLiveShopping.queue = []

    const scriptNode = document.createElement('script')
    scriptNode['src'] = mint.services.bambuser.src

    document.body.appendChild(scriptNode)
  },

  /**
   * Configures the Bambuser player.
   * @param {Object} player - The Bambuser player object.
   */
  configurePlayer(player) {
    player.configure({
      currency: mint.shop.currency,
      enableFirstPartyCookies: mint.customer.canBeTracked,
      locale: `${mint.shop.language}-${mint.shop.country}`,
      buttons: {
        dismiss: player.BUTTON.CLOSE,
      },
      cookie: {
        domain: window.location.host,
        activityCookieTTLDays: Number(mint.services.bambuser.activityCookieDays) || 7,
      },
    })
  },

  /**
   * Initializes Bambuser Live Shopping event subscriptions.
   * @param {Object} data - The data object containing configuration options.
   */
  subscribeOnBambuserEvents(data) {
    const { preventDrawer = true, trackingParameters = {} } = data
    const bambuserCartItems = []

    window.onBambuserLiveShoppingReady = (player) => {
      this.configurePlayer(player)
      this.subscribeToCartEvents(
        player,
        bambuserCartItems,
        preventDrawer,
        trackingParameters,
      )
      this.subscribeToCheckoutEvent(player)
      this.subscribeToProductDataEvent(player)
    }
  },

  /**
   * Subscribes to cart-related events.
   * @param {Object} player - The Bambuser player object.
   * @param {Array} bambuserCartItems - Array to store Bambuser cart items.
   * @param {boolean} preventDrawer - Whether to prevent cart drawer.
   * @param {Object} trackingParameters - Tracking parameters.
   */
  subscribeToCartEvents(
    player,
    bambuserCartItems,
    preventDrawer,
    trackingParameters,
  ) {
    player.on(player.EVENT.ADD_TO_CART, (addedItem, callback) => {
      const itemID = addedItem.sku
      const matching = this.getProductVariantFromId(itemID)

      const data = {
        id: itemID,
        quantity: 1,
      }

      data.properties = getItemProperties(matching.product, matching.variant)

      /**
       * Cart payload.
       */
      const payload = {
        data,
        meta: {
          section: trackingParameters.section,
        },
        preventDrawer: preventDrawer,
      }

      /**
       * Removing previously attached cart listeners, as we add them for a specific
       * event / request.
       */
      this.removeCartEventListeners()

      /**
       * Adding new success/fail listeners for the following `cart:addItems`
       * event.
       */
      mint.EventBus.on('cart:added', (response) => {

        this.handleCartAddedResponse(
          response,
          itemID,
          bambuserCartItems,
          callback,
        )
      })

      mint.EventBus.on('cart:addFailed', (response) => {
        callback({ success: false, reason: 'out-of-stock' })
      })

      mint.EventBus.dispatch('cart:addItems', payload)
    })

    player.on(player.EVENT.UPDATE_ITEM_IN_CART, (updatedItem, callback) => {
      const cartItem = bambuserCartItems.find(
        (item) => item.id === updatedItem.sku,
      )
      const itemKey = cartItem?.key || updatedItem.sku

      const payload = {
        data: {
          id: itemKey,
          quantity: updatedItem.quantity,
        },
      }

      this.handleCartItemUpdate(
        updatedItem,
        itemKey,
        payload,
        callback,
      )
    })

    player.on(player.EVENT.SYNC_CART_STATE, () => {
      this.handleSyncCartState(player)
    })
  },

  /**
   * Subscribes to the checkout event.
   * @param {Object} player - The Bambuser player object.
   */
  subscribeToCheckoutEvent(player) {
    player.on(player.EVENT.CHECKOUT, () => {
      const checkoutUrl = mint.urls.checkout

      player.showCheckout(checkoutUrl)
    })
  },

  /**
   * Subscribes to the product data event.
   * @param {Object} player - The Bambuser player object.
   */
  subscribeToProductDataEvent(player) {
    player.on(player.EVENT.PROVIDE_PRODUCT_DATA, (event) => {
      for (const eventProduct of event.products) {
        const bambuserId = eventProduct.id
        const handle = eventProduct.url.split('products/')[1].split('?')[0]

        mint
          .GetProduct({ handle })
          .then((product) => {
            this.handleProductHydration(player, bambuserId, product, handle)
          })
          .catch((error) => {
            mint.Log('warning', 'Unable to hydrate Bambuser product', error, {
              handle,
            })
          })
      }
    })
  },

  /**
   * Handles the response when an item is successfully added to the cart.
   * @param {Object} response - The response object.
   * @param {string} itemID - The ID of the added item.
   * @param {Array} bambuserCartItems - Array to store Bambuser cart items.
   * @param {Function} callback - The callback function from player event.
   */
  handleCartAddedResponse(response, itemID, bambuserCartItems, callback) {
    const itemKey = response.response?.key

    if (!bambuserCartItems.find((item) => item.id === itemID)) {
      bambuserCartItems.push({
        id: itemID,
        key: itemKey,
      })
    }

    callback(true)
  },

  /**
   * Handles updating a cart item.
   * @param {Object} updatedItem - The updated item object.
   * @param {string} itemKey - The key of the item in the cart.
   * @param {Object} payload - The payload to be dispatched.
   * @param {Function} callback - The callback function from player event.
   */
  handleCartItemUpdate(
    updatedItem,
    itemKey,
    payload,
    callback,
  ) {

    /**
     * Removing previously added cart listeners, as we add them for a specific
     * event / request.
     */
    this.removeCartEventListeners()

    /**
     * Adding new success/fail listeners for the following `cart:removeItem` or
     * `cart:changeItem` event.
     */
    mint.EventBus.on('cart:changed', () => {
      callback(true)
    })

    mint.EventBus.on('cart:removed', () => {
      callback(true)
    })

    mint.EventBus.on('cart:addFailed', () => {
      callback({ success: false, reason: 'out-of-stock' })
    })

    mint.EventBus.on('cart:changeFailed', (response) => {
      callback({
        success: false,
        reason: 'out-of-stock',
        message: response.response?.message || 'Out of stock.',
      })
    })

    if (updatedItem.quantity === 0) {
      mint.EventBus.dispatch('cart:removeItem', itemKey)
    } else {
      mint.EventBus.dispatch('cart:changeItem', payload)
    }
  },

  /**
   * Handles syncing the cart state.
   * @param {Object} player - The Bambuser player object.
   */
  handleSyncCartState(player) {
    mint.EventBus.dispatch('cart:fetch')

    /**
     * Note:
     * - "Currently, updateCart only supports emptying the cart"
     * - https://bambuser.com/docs/one-to-many/player-api-reference/#playerupdatecartcartdata
     */
    mint.EventBus.on('mint:cart:updated', (response) => {
      if (response.item_count === 0) {
        player.updateCart({
          items: [],
        })
      }
    })
  },

  /**
   * Handles successful retrieval of product data.
   * @param {Object} player - The Bambuser player object.
   * @param {string} bambuserId - The ID of the Bambuser product.
   * @param {Object} product - The formatted product object.
   * @param {string} handle - Product handle.
   */
  handleProductHydration(player, bambuserId, product, handle) {
    if (!product || !product?.available) {
      player.updateProduct(bambuserId, (productFactory) =>
        productFactory.inheritFromPlaceholder().hidden(true),
      )

      mint.Log(
        'warning',
        '[Bambuser] Fail to get product data',
        false,
        {
          country: mint.shop.countryName,
          handle,
          product,
        },
      )

    } else {
      // Construct product details
      const images = product.images.map((image) => {
        return `${location.protocol}${image.src}`
      })

      this.state.products.push(product)

      player.updateProduct(bambuserId, (productFactory) =>
        productFactory.product((detailsFactory) =>
          detailsFactory
            .name(product.title)
            .sku(product.id)
            .brandName(mint.shop.name)
            .introduction(product.whyWeLoveIt)
            .defaultVariationIndex(0)
            .variations((variationFactory) =>
              product.variants.map((variation) =>
                variationFactory()
                  .imageUrls(images)
                  .name(variation.title)
                  .sku(product.id)
                  .sizes((sizeFactory) => [
                    sizeFactory()
                      .name(variation.title)
                      .sku(variation.id)
                      .inStock(variation.available)
                      .price((priceFactory) =>
                        priceFactory
                          .currency(mint.shop.currency)
                          .original(variation.compareAtPrice.amount / 100)
                          .current(variation.price.amount / 100),
                      ),
                  ]),
              ),
            ),
        ),
      )
    }
  },

  /**
   * Set Event Bus Listeners.
   */
  setEventBusListeners() {

    /**
     * Initializes Bambuser Live Shopping with the given item.
     * @param {Object} payload - The options object containing information about the show.
     * @param {string} payload.showId - The ID of the Bambuser show to be embedded.
     * @param {HTMLElement} payload.node - The trigger element for opening the specified show.
     * @param {string} [payload.type] - The type of embedding. Defaults to 'overlay'.
     */
    mint.EventBus.on('bambuser:initLiveShopping', (payload) => {
      if (!window.initBambuserLiveShopping) {
        mint.Log('error', 'Fail: Bambuser isn\'t initialised', new Error('Bambuser not initialised'), payload)

        return
      }

      const { showId, node, type } = payload

      window.initBambuserLiveShopping({
        showId,
        node,
        type,
      })
    })
  },

  /**
   * Removes previously attached cart-related event listeners.
   */
  removeCartEventListeners() {
    const eventsToRemove = [
      'cart:added',
      'cart:addFailed',
      'cart:changed',
      'cart:changeFailed',
      'cart:removed',
      'mint:cart:updated',
    ]

    eventsToRemove.forEach((event) => {
      mint.EventBus.remove(event)
    })
  },

  /**
   * Store the products added to the event in local state, for use after.
   * @param {Object} product - Formatted Shopify product.
   */
  setProductState(product) {
    const productIsStored = this.state.products.find((storedProduct) => {
      return product.handle === storedProduct.handle
    })

    if (productIsStored) {
      return
    }

    this.state.products.push(product)
  },

  /**
   * Return a formatted product and active variant product for a given variant ID.
   * @param {Number|String} variantId - Valid Shopify ID
   * @returns {Object}
   */
  getProductVariantFromId(variantId) {
    const response = {}

    response.product = this.state.products.find((product) => {
      const matchingVariant = product.variants.find((variant) => {
        return Number(variant.id) === Number(variantId)
      })

      if (matchingVariant) {
        response.matchingVariant = matchingVariant
      }

      response.variant = matchingVariant

      return matchingVariant || false
    })

    return response
  },

  /**
   * Generate an array of strings to describe any options containing
   * international sizing values, for use as a line item property.
   * @param {Array} options - Product options with name and value properties.
   */
  getSizingProperties(options = []) {
    const sizingOptions =
      options.filter(option => {
        return stringToHandle(option.name) === 'size'
      })

    return sizingOptions.map(option => {
      return `${option.name}:${option.value}`
    })
  },

  /**
   * Initializes the Bambuser API.
   * - Use `subscribeOnBambuserEvents` method after invoking `init()` to pass
   * custom data.
   */
  init() {
    this.initializeBambuserScript()
    this.setEventBusListeners()

    this.state = {
      products: [],
    }

    /**
     * Register Bambuser Button element.
     */
    if ('customElements' in window && !customElements.get('bambuser-button')) {
      customElements.define('bambuser-button', BambuserButton)
    }
  },
}
